/**
 * Root component for React Router v7
 *
 * Wraps all routes with necessary providers that need to be inside RouterProvider.
 * Context, PdeContext, and HeadProvider are in entry points (outside RouterProvider).
 * Route-specific providers (Advertising, Authentication, LoginModal) must be here
 * so they're accessible to all child routes through <Outlet />.
 *
 * Mirrors the legacy `components/App/index.js` provider tree so advertising works:
 * MotorCmp initializes Didomi (without it Publift never gets consent → no creatives),
 * and AdvertisingRoadblock renders the top/right SaitamaAd slots from Root mount so
 * Publift Fuse finds them in the DOM when `pageInit` runs.
 */
import {type ReactNode, useContext} from 'react'
import {Outlet, useLocation} from 'react-router'

import {useDecision} from '@s-ui/pde'
import Context from '@s-ui/react-context'
import WebVitalsReporter from '@s-ui/react-web-vitals'
import {AdvertisingProvider} from '@adv-mt/advertising-publift'
import AdvertisingRoadblock from '@adv-mt/mt-advertising-roadblock'
import {AuthenticationProvider} from '@adv-mt/mt-context-authentication'
import {LoginModalProvider} from '@adv-mt/mt-context-login-modal'
import {NetLoadingProvider} from '@adv-mt/mt-net-drawer'
import MotorCmp from '@adv-mt/mt-shared-cmp'

import {WEB_VITALS_PATHNAMES} from './config/index.js'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS helper without type declarations
import {isMatchingRoute} from './helpers/index.js'

// Coches Ad Detail URL pattern (mirrors app/src/routes.tsx). AdvertisingRoadblock uses
// this flag to pick the correct slot layout on ad-detail pages.
const ADS_DETAIL_COCHES_REGEX = /\/.*-(?<adId>\d{4,9})-[a-z]{2,4}(vo|vn)\.aspx(\?.*)?$/
const ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX = /\/renting\/.*-(?<adId>\d{4,9})-vsre(\?.*)?$/
const ADS_DETAIL_MOTOS_REGEX = /\/(ocasion|nuevas)\/.*-(?<adId>\d{5,8})\.htm(\?.*)?$/

// Allow disabling the CMP for e2e / lighthouse via `?disableCmp=true`, matching the
// legacy convention.
const DISABLE_CMP_QUERYPARAM = 'disableCmp'

// `outlet` lets routes.tsx swap <Outlet /> for the navigation skeleton (see NavigationSkeletonOutlet).
export function Root({outlet = <Outlet />}: {outlet?: ReactNode}) {
  const context = useContext(Context)
  const location = useLocation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {domain} = context ?? {}
  const config = domain?.get('config')

  const advertisingEnvironment = config?.get('ADVERTISING_ENV')
  const {ADVERTISING_SITE} = config?.get('advertising') ?? {}
  // The `ff_web_adit_publift_simplified` decision chose between publift and
  // saitama in legacy. In this Vite fork all pages import `SaitamaAd` /
  // `useAdvertising` from `@adv-mt/advertising-publift` unconditionally, so the
  // provider must be the publift one — otherwise pages call `useAdvertising`
  // against a provider they weren't expecting and SSR blows up with
  // "useAdvertising must be used within an AdvertisingProvider". PDE isn't
  // reliably ready during SSR either, which used to silently default to
  // saitama and break the pages that only use publift.

  // CMP (Didomi) — required for advertising to request creatives. Publift/Saitama wait
  // for `__tcfapi` consent before firing GPT requests; without <MotorCmp /> the CMP stays
  // as the empty stub injected by index.html and Publift never gets consent → 0 ads.
  const searchParams = new URLSearchParams(location.search)
  const disableCmpByQueryParam = Boolean(searchParams.get(DISABLE_CMP_QUERYPARAM))
  const shouldShowCmp = !config?.get('isWebview') && !disableCmpByQueryParam

  // Ad rendering — mirror legacy `components/App/index.js`. AdvertisingRoadblock renders
  // <SaitamaAd> for pubTop1/pubRight1/pubRight2 from Root mount so Publift Fuse has slots
  // in the DOM when its `pageInit` runs. Without this, `SaitamaAd` in a page mounts too
  // late and Fuse ends up calling pageInit with an empty blockingFuseIds list.
  const pageConfig = config?.get('page') ?? {}
  const {PAGES_WITHOUT_ADS = [], ADS_LIST_FLATTENED_PATH_PATTERNS = []} = pageConfig
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isListing = (ADS_LIST_FLATTENED_PATH_PATTERNS as any[]).some((route: any) =>
    isMatchingRoute({pathName: location.pathname, route})
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shouldDisplayAds = !(PAGES_WITHOUT_ADS as any[]).some((route: any) =>
    isMatchingRoute({pathName: location.pathname, route})
  )
  const isCochesAdDetailsPage = Boolean(location.pathname?.match(ADS_DETAIL_COCHES_REGEX))
  const isCochesAdSubscriptionDetailPage = Boolean(location.pathname?.match(ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX))
  const isMotosAdDetailsPage = Boolean(location.pathname?.match(ADS_DETAIL_MOTOS_REGEX))
  // The revamped detail places its own ads (top banner, inline, gallery), so the roadblock must stay
  // out of it on both tenants. Without this the page renders pubTop1 twice (MTR-120758).
  const {enabled: isAdDetailRevampEnabled} = useDecision('ff_web_buyer_ad_detail_revamp')
  const isAdDetailRevamp =
    (isCochesAdDetailsPage || isCochesAdSubscriptionDetailPage || isMotosAdDetailsPage) &&
    Boolean(isAdDetailRevampEnabled)

  const advertisingChildren =
    shouldDisplayAds && !isAdDetailRevamp ? (
      <AdvertisingRoadblock isListing={isListing} isAdDetailPage={isCochesAdDetailsPage}>
        {outlet}
        {shouldShowCmp && <MotorCmp />}
      </AdvertisingRoadblock>
    ) : (
      <>
        {outlet}
        {shouldShowCmp && <MotorCmp />}
      </>
    )

  return (
    // WebVitalsReporter sends the Core Web Vitals (adv.logger PERFORMANCE_LOG, `cwv.*`) of the allowed
    // pages, as `components/App` did in the legacy app; without it Vite sent none.
    // NetLoadingProvider shares "a Net answer is in flight / just arrived" between the Net drawer and
    // the buttons that open it (topbar, ad detail star). Without a provider `useNetLoading` silently
    // returns no-ops and none of them animate or show the badge. The legacy `providerFactory` mounted it.
    <WebVitalsReporter allowed={WEB_VITALS_PATHNAMES}>
      <NetLoadingProvider>
        <AdvertisingProvider site={ADVERTISING_SITE} environment={advertisingEnvironment} isMushroomInitialized>
          <AuthenticationProvider>
            <LoginModalProvider>{advertisingChildren}</LoginModalProvider>
          </AuthenticationProvider>
        </AdvertisingProvider>
      </NetLoadingProvider>
    </WebVitalsReporter>
  )
}

export default Root
